import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Book as BookIcon,
    Translate as TranslateIcon,
    MenuBook as MenuBookIcon,
    Psychology as PsychologyIcon,
    Headphones as HeadPhonesIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { sutraApi } from '../services/sutras';
import { Sutra, Project } from '../types/sutra';
import ConfirmDialog from './ConfirmDialog';

const SutraList: React.FC = () => {
    const [sutras, setSutras] = useState<Sutra[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<string>(() => localStorage.getItem('selectedProject') || '');
    const [selectedChapter, setSelectedChapter] = useState<number>(() => Number(localStorage.getItem('selectedChapter')) || 0);
    const [selectedSection, setSelectedSection] = useState<number>(() => Number(localStorage.getItem('selectedSection')) || 0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingSutra, setEditingSutra] = useState<Sutra | null>(null);
    const [formData, setFormData] = useState<Sutra>({ id: 0, section: 0, chapter: 0, number: 0, text: '', project_id: 1 });
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [sutraToDelete, setSutraToDelete] = useState<Sutra | null>(null);
    const navigate = useNavigate();
    const [availableChapters, setAvailableChapters] = useState<number[]>([]);
    const [availableSections, setAvailableSections] = useState<number[]>([]);

    useEffect(() => { if (selectedProject) localStorage.setItem('selectedProject', selectedProject); }, [selectedProject]);
    useEffect(() => { localStorage.setItem('selectedChapter', selectedChapter.toString()); }, [selectedChapter]);
    useEffect(() => { localStorage.setItem('selectedSection', selectedSection.toString()); }, [selectedSection]);

    const fetchProjects = useCallback(async () => {
        try {
            setLoading(true);
            const data = await sutraApi.getProjects();
            setProjects(data);
            if (data.length > 0 && !selectedProject) setSelectedProject(data[0].name);
        } catch {
            setError('Failed to fetch projects');
        } finally {
            setLoading(false);
        }
    }, [selectedProject]);

    useEffect(() => { fetchProjects(); }, [fetchProjects]);

    useEffect(() => {
        const updateChaptersAndSutras = async () => {
            if (!selectedProject) return;
            try {
                setLoading(true);
                const allSutras = await sutraApi.getSutras(selectedProject);
                const sutrasWithDetails = await Promise.all(allSutras.map(async sutra => {
                    try {
                        return await sutraApi.getSutraById(selectedProject, sutra.id);
                    } catch {
                        return { ...sutra, section: 0, chapter: 0 };
                    }
                }));
                const sections = Array.from(new Set(sutrasWithDetails.map(s => s.section))).sort();
                setAvailableSections(sections);
                if (sections.length > 0 && !sections.includes(selectedSection)) setSelectedSection(sections[0]);
                const chapters = Array.from(new Set(sutrasWithDetails.map(s => s.chapter ?? 0))).sort((a, b) => a - b);
                setAvailableChapters(chapters);
                if (!chapters.includes(selectedChapter)) setSelectedChapter(0);
                let filteredData = sutrasWithDetails;
                if (selectedSection > 0) {
                    /*if (selectedSection)*/ filteredData = filteredData.filter(s => s.section === selectedSection);
                    if (selectedChapter > 0) filteredData = filteredData.filter(s => s.chapter === selectedChapter)
                        else filteredData = filteredData.filter(s => s.chapter === 0);
                } else {
                    filteredData = filteredData.filter(s => s.section === selectedSection);
                    // filteredData = filteredData.filter(s => s.chapter === selectedChapter);
                    if (selectedChapter > 0) filteredData = filteredData.filter(s => s.chapter === selectedChapter)
                        else filteredData = filteredData.filter(s => s.chapter === 0);
                }
                filteredData = filteredData.sort((a, b) => (a.chapter - b.chapter) ? (a.chapter - b.chapter) : (a.number - b.number));
                // filteredData = filteredData.sort((a, b) => a.number - b.number);
                const sutrasWithText = await Promise.all(filteredData.map(async sutra => {
                    try {
                        const chapter = sutra.chapter ?? 0;
                        const details = await sutraApi.getSutra(selectedProject, sutra.section, chapter, sutra.number);
                        return { ...sutra, text: details.text };
                    } catch {
                        return { ...sutra, text: 'Error loading text' };
                    }
                }));
                setSutras(sutrasWithText);
            } catch {
                setError('Failed to fetch sutras');
            } finally {
                setLoading(false);
            }
        };
        updateChaptersAndSutras();
    }, [selectedProject, selectedSection, selectedChapter]);

    const handleOpenDialog = (sutra?: Sutra) => {
        if (sutra) {
            setEditingSutra(sutra);
            setFormData(sutra);
        } else {
            setEditingSutra(null);
            setFormData({ id: 0, section: selectedSection || 0, chapter: selectedChapter || 0, number: 0, text: '', project_id: projects.find(p => p.name === selectedProject)?.id || 1 });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingSutra(null);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            if (editingSutra) {
                await sutraApi.updateSutra(selectedProject, formData.section, formData.chapter, formData.number, formData);
            } else {
                await sutraApi.createSutra(selectedProject, formData.section, formData);
            }
            handleCloseDialog();
            fetchProjects();
        } catch {
            setError('Failed to save sutra');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDelete = (sutra: Sutra) => {
        setSutraToDelete(sutra);
        setConfirmDialogOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        if (!sutraToDelete) return;
        try {
            setLoading(true);
            const chapter = sutraToDelete.chapter ?? 0;
            await sutraApi.deleteSutra(selectedProject, sutraToDelete.section, chapter, sutraToDelete.number);
            fetchProjects();
        } catch {
            setError('Failed to delete sutra');
        } finally {
            setLoading(false);
            setConfirmDialogOpen(false);
            setSutraToDelete(null);
        }
    };

    return (
        <div>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Project</InputLabel>
                <Select value={selectedProject} label="Project" onChange={(e) => setSelectedProject(e.target.value)}>
                    {projects.map((project) => (
                        <MenuItem key={project.id} value={project.name}>{project.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Section</InputLabel>
                <Select value={selectedSection} label="Section" onChange={(e) => setSelectedSection(Number(e.target.value))}>
                    {availableSections.map((section) => (
                        <MenuItem key={section} value={section}>{section}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Chapter</InputLabel>
                <Select value={selectedChapter} label="Chapter" onChange={(e) => setSelectedChapter(Number(e.target.value))}>
                    <MenuItem value={0}>All Chapters</MenuItem>
                    {availableChapters.map((chapter) => (
                        <MenuItem key={chapter} value={chapter}>Chapter {chapter}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>Add New Sutra</Button>
            {loading && <CircularProgress sx={{ mt: 2 }} />}
            {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Section</TableCell>
                            <TableCell>Chapter</TableCell>
                            <TableCell>Number</TableCell>
                            <TableCell>Text</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sutras.map((sutra) => (
                            <TableRow key={`${selectedProject}-${sutra.section ?? ''}-${sutra.chapter ?? 0}-${sutra.number}`}>
                                <TableCell>{sutra.section}</TableCell>
                                <TableCell>{sutra.chapter ?? 0}</TableCell>
                                <TableCell>{sutra.number}</TableCell>
                                <TableCell>{sutra.text || 'Loading...'}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleOpenDialog(sutra)}><EditIcon /></IconButton>
                                    <IconButton onClick={() => handleConfirmDelete(sutra)}><DeleteIcon /></IconButton>
                                    <IconButton onClick={() => navigate(`/meanings/${selectedProject}/${sutra.section ?? ''}/${sutra.chapter ?? 0}/${sutra.number}`)}><BookIcon /></IconButton>
                                    <IconButton onClick={() => navigate(`/transliterations/${selectedProject}/${sutra.section ?? ''}/${sutra.chapter ?? 0}/${sutra.number}`)}><TranslateIcon /></IconButton>
                                    <IconButton onClick={() => navigate(`/projects/${selectedProject}/${sutra.section ?? ''}/sutras/${sutra.chapter ?? 0}/${sutra.number}/interpretations`)}><MenuBookIcon /></IconButton>
                                    <IconButton onClick={() => navigate(`/projects/${selectedProject}/${sutra.section ?? ''}/sutras/${sutra.chapter ?? 0}/${sutra.number}/bhashyams`)}><PsychologyIcon /></IconButton>
                                    <IconButton onClick={() => navigate(`/projects/${selectedProject}/${sutra.section ?? ''}/sutras/${sutra.chapter ?? 0}/${sutra.number}/audios`)}><HeadPhonesIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>{editingSutra ? 'Edit Sutra' : 'Add New Sutra'}</DialogTitle>
                <DialogContent>
                    <TextField margin="dense" label="Section" type="number" fullWidth value={formData.section} onChange={(e) => setFormData({ ...formData, section: parseInt(e.target.value) })} />
                    <TextField margin="dense" label="Chapter" type="number" fullWidth value={formData.chapter} onChange={(e) => setFormData({ ...formData, chapter: parseInt(e.target.value) })} />
                    <TextField margin="dense" label="Number" type="number" fullWidth value={formData.number} onChange={(e) => setFormData({ ...formData, number: parseInt(e.target.value) })} />
                    <TextField margin="dense" label="Text" fullWidth multiline rows={4} value={formData.text} onChange={(e) => setFormData({ ...formData, text: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} color="primary" disabled={loading}>{editingSutra ? 'Update' : 'Create'}</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmDialogOpen}
                title="Confirm Delete"
                message={`Are you sure you want to delete Sutra ${sutraToDelete?.number}?`}
                onConfirm={handleDeleteConfirmed}
                onCancel={() => setConfirmDialogOpen(false)}
                confirmText="Delete"
                cancelText="Cancel"
            />
        </div>
    );
};

export default SutraList;